import { Auth } from "common";
import { TOTPSetup } from "common/src/components/TOTPSetup";
import { SignInOutput } from "common/src/utils/amplifyBootstrap";
import { FormikProvider, useFormik } from "formik";
import { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Button, Text, YStack, Image, Separator, View } from "tamagui";
import { GradientButton, OutlinedButton } from "tamagui-config";

import logoImage from "../assets/images/HG-full-logo.svg";
import { FormikInput } from "../components/FormikInput";
import { GlowCircle } from "../components/GlowCircle";
import { AmplifyContext } from "../contexts/AmplifyContext";
import useFieldValidationSchema from "../hooks/useFieldValidationSchema";

const Login = () => {
  const navigate = useNavigate();

  const [showTotpSetup, setShowTotpSetup] = useState(false);
  const [totpError, setTotpError] = useState<string | null>(null);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [signInContext, setSignInContext] = useState<SignInOutput | null>(null);

  const handleTotpSetupComplete = () => {
    console.log("TOTP setup initiated");
  };

  const handleTotpVerifyComplete = () => {
    navigate("/", { replace: true });
  };

  return (
    <View
      position="relative"
      minHeight="100vh"
      alignItems="center"
      justifyContent="center"
      backgroundColor="black"
    >
      <View position="absolute" width="100%" height="100%">
        <GlowCircle />
      </View>

      {showTotpSetup && !showNewPasswordForm && (
        <TOTPSetup
          onSetupComplete={handleTotpSetupComplete}
          onVerifyComplete={handleTotpVerifyComplete}
          onCancel={() => {
            setShowTotpSetup(false);
            setTotpError(
              "MFA setup was canceled. You must set up MFA to continue.",
            );
          }}
        />
      )}

      {!showTotpSetup && showNewPasswordForm && (
        <ChangePasswordForm
          signInContext={signInContext}
          onTotpRequested={() => {
            setShowTotpSetup(true);
            setShowNewPasswordForm(false);
          }}
        />
      )}

      {!showTotpSetup && !showNewPasswordForm && (
        <LoginForm
          onFirstStepCompleted={setSignInContext}
          onTotpRequested={() => setShowTotpSetup(true)}
          onPasswordChangeRequested={() => setShowNewPasswordForm(true)}
        />
      )}

      {totpError && (
        <Text color="$red10" fontSize="$2">
          {totpError}
        </Text>
      )}
    </View>
  );
};

const LoginForm = ({
  onFirstStepCompleted,
  onTotpRequested,
  onPasswordChangeRequested,
}: {
  onFirstStepCompleted: (context: SignInOutput) => void;
  onTotpRequested: () => void;
  onPasswordChangeRequested: () => void;
}) => {
  const { signInWithRedirect } = useContext(AmplifyContext);
  const { loginValidationSchema } = useFieldValidationSchema();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleEmailPasswordSignIn = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    setIsLoggingIn(true);
    setLoginError(null);

    try {
      const result = await Auth.signIn(email, password);

      onFirstStepCompleted(result);

      if (
        result.nextStep?.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        onPasswordChangeRequested();
        return;
      }

      if (result.nextStep?.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
        onTotpRequested();
        return;
      }

      if (result.isSignedIn) {
        navigate("/", { replace: true });
      }
    } catch (error) {
      console.error("Sign-in error:", error);
      setLoginError(
        error instanceof Error
          ? error.message
          : "An error occurred during sign in",
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithRedirect({ provider: "Google" });
    } catch (error) {
      console.error("Error signing in with Google", error);
      setLoginError(t("auth.googleSignInError"));
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    enableReinitialize: true,
    validationSchema: loginValidationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: handleEmailPasswordSignIn,
  });

  return (
    <YStack gap="$8" width={362} maxWidth="100%" alignItems="center">
      <Image
        source={{ uri: logoImage, width: 312, height: 172 }}
        maxWidth="100%"
        resizeMode="stretch"
        alt="Heartbeat Logo"
      />
      <YStack width="100%">
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <YStack gap="$3.5">
              <FormikInput
                fieldName="email"
                placeholder={t("auth.email")}
                autoComplete="email"
                autoCapitalize="none"
                keyboardType="email-address"
                width="100%"
              />
              <FormikInput
                fieldName="password"
                placeholder={t("auth.password")}
                autoComplete="password"
                autoCapitalize="none"
                isPassword
              />

              <YStack gap="$4">
                <GradientButton
                  elevated
                  rounded
                  loading={isLoggingIn}
                  onPress={() => formik.handleSubmit()}
                >
                  {t("auth.signIn")}
                </GradientButton>
                <Button
                  variant="outlined"
                  disabled={isLoggingIn}
                  onPress={(event) => {
                    event.preventDefault();
                    navigate("/forgot-password");
                  }}
                >
                  {t("auth.forgotPassword")}
                </Button>
              </YStack>

              {loginError && (
                <Text color="$red10" fontSize="$2">
                  {loginError}
                </Text>
              )}

              <Separator flex={1} />

              <OutlinedButton
                rounded
                onPress={(event) => {
                  event.preventDefault();
                  handleGoogleSignIn();
                }}
              >
                {t("auth.signInWithGoogle")}
              </OutlinedButton>
            </YStack>
          </form>
        </FormikProvider>
      </YStack>
    </YStack>
  );
};

const ChangePasswordForm = ({
  signInContext,
  onTotpRequested,
}: {
  signInContext: SignInOutput | null;
  onTotpRequested: () => void;
}) => {
  const { requestPasswordChangeValidationSchema } = useFieldValidationSchema();
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleNewPasswordSubmit = async ({ name, newPassword }) => {
    setLoginError(null);
    setIsSettingPassword(true);

    try {
      if (!signInContext) {
        throw new Error("Sign-in context is missing. Please sign in again.");
      }

      const result = await (Auth.confirmSignIn as any)({
        challengeResponse: newPassword,
        options: {
          userAttributes: {
            name,
          },
        },
        context: signInContext,
      });

      if (result.nextStep?.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
        onTotpRequested();
        return;
      }

      if (result.isSignedIn) {
        navigate("/", { replace: true });
      }
    } catch (error) {
      console.error("New password error:", error);
      setLoginError(
        error instanceof Error
          ? error.message
          : "An error occurred while setting new password",
      );
    } finally {
      setIsSettingPassword(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      newPassword: "",
      repeatedNewPassword: "",
    },
    enableReinitialize: true,
    validationSchema: requestPasswordChangeValidationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: handleNewPasswordSubmit,
  });

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <YStack
          gap="$6"
          width={473}
          maxWidth="100%"
          padding="$7"
          backgroundColor="$transparentBorder"
          borderWidth={2}
          borderColor="$transparentBorder"
          borderRadius="$8"
        >
          <Text fontWeight="bold" color="$white12" textAlign="center">
            {t("auth.newPasswordRequired")}
          </Text>

          <Separator />

          <YStack gap="$3.5" padding="$3.5">
            <YStack gap="$2.5" paddingHorizontal="$5" paddingBottom="$3.5">
              <Text
                fontSize="$7.5"
                fontWeight="bold"
                color="$white12"
                textAlign="center"
              >
                {t("auth.newPasswordRequired")}
              </Text>
              <Text color="$white10" textAlign="center">
                {t("auth.newPasswordRequiredDescription")}
              </Text>
            </YStack>

            <FormikInput
              fieldName="name"
              placeholder={t("auth.name")}
              autoCapitalize="words"
            />
            <FormikInput
              fieldName="newPassword"
              placeholder={t("auth.newPassword")}
              autoCapitalize="none"
              isPassword
            />
            <FormikInput
              fieldName="repeatedNewPassword"
              placeholder={t("auth.repeatedNewPassword")}
              autoCapitalize="none"
              isPassword
            />

            <GradientButton
              rounded
              width="100%"
              loading={isSettingPassword}
              onPress={() => formik.handleSubmit()}
            >
              {t("auth.setNewPassword")}
            </GradientButton>

            {loginError && (
              <Text color="$red10" fontSize="$2">
                {loginError}
              </Text>
            )}
          </YStack>
        </YStack>
      </form>
    </FormikProvider>
  );
};

export default Login;
